<template>
  <div class="wonde-student-management-config">
    <v-row no-gutters>
      <v-col cols="12">
        <span>API Endpoint</span>
        <span class="text-red">*</span>
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="txt_apiEndpoint"
          v-model="form.apiEndpoint"
          variant="solo"
          density="compact"
          placeholder="API Endpoint"
          :error-messages="errors.apiEndpoint"
          :disabled="serviceEnded"
          @update:model-value="onTextInput('apiEndpoint', 'API Endpoint')"
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>School ID</span>
        <span class="text-red">*</span>
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="txt_schoolId"
          v-model="form.schoolId"
          variant="solo"
          density="compact"
          placeholder="School ID"
          :error-messages="errors.schoolId"
          :disabled="serviceEnded"
          @update:model-value="onTextInput('schoolId', 'School ID')"
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>Token Key</span>
        <span class="text-red">*</span>
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="txt_tokenKey"
          v-model="form.tokenKey"
          variant="solo"
          density="compact"
          placeholder="Token Key"
          :error-messages="errors.tokenKey"
          :disabled="serviceEnded"
          @update:model-value="onTextInput('tokenKey', 'Token Key')"
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>Client-to-STB Medical Condition Map</span>
      </v-col>
      <v-col cols="12">
        <v-textarea
          id="txt_clientToStbMedicalConditionMap"
          v-model="form.clientToStbMedicalConditionMap"
          variant="solo"
          density="compact"
          placeholder="Client-to-STB Medical Condition Map"
          :error-messages="errors.clientToStbMedicalConditionMap"
          :disabled="serviceEnded"
          @update:model-value="
            onJsonInput('clientToStbMedicalConditionMap', 'Client-to-STB Medical Condition Map')
          "
        ></v-textarea>
      </v-col>
    </v-row>

    <v-row no-gutters class="pt-4">
      <v-col cols="12">
        <cs-button
          id="btnTestConnection"
          primary
          name="btnTestConnection"
          :disabled="testConnectionButtonDisabled"
          :loading="testConnectionInProgress"
          label="Test Connection"
          class="ml-0 my-0"
          @click="onClickTestConnection()"
        ></cs-button>
        <v-label :class="testConnectionFailedStatus.length > 0 ? 'generic-error-message' : ''">
          {{ testConnectionStatusDisplay }}
        </v-label>
      </v-col>
    </v-row>

    <cs-form-dialog
      id="dlg-wonde-test-connection-generic"
      v-model="genericDialogDisplayed"
      body-id="dlg-wonde-test-connection-generic-body"
      heading="Student Management"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="genericDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <div
          v-safe-html="genericDialogMessage"
          :class="isGenericDialogDisplayedAsError ? 'generic-error-message' : ''"
        ></div>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import * as types from '@/store/mutationTypes';
import { cloneDeep, has } from 'lodash';

const REQUIRED_TEXT_FIELDS = ['apiEndpoint', 'schoolId', 'tokenKey'];

export default {
  name: 'WondeStudentManagementConfig',

  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    serviceEnded: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:config', 'update:error-hash', 'update:valid'],

  data() {
    return {
      form: {
        apiEndpoint: '',
        schoolId: '',
        tokenKey: '',
        clientToStbMedicalConditionMap: ''
      },
      errors: {
        apiEndpoint: [],
        schoolId: [],
        tokenKey: [],
        clientToStbMedicalConditionMap: []
      },
      errorHash: {},
      testConnectionInProgress: false,
      testConnectionAt: '',
      testConnectionFailedStatus: '',
      genericDialogDisplayed: false,
      isGenericDialogDisplayedAsError: false,
      genericDialogMessage: ''
    };
  },

  computed: {
    testConnectionButtonDisabled() {
      return !this.isValid() || this.testConnectionInProgress;
    },
    testConnectionStatusDisplay() {
      if (this.testConnectionFailedStatus.length === 0 && this.testConnectionAt.length > 0) {
        return `Last Succeed At: ${this.$app
          .moment(this.testConnectionAt)
          .format('YYYY-MM-DD HH:mm:ss')}.`;
      }

      if (this.testConnectionAt.length > 0) {
        return `Last Failed At: ${this.$app
          .moment(this.testConnectionAt)
          .format('YYYY-MM-DD HH:mm:ss')}. ${this.testConnectionFailedStatus}`;
      }

      return '';
    }
  },

  watch: {
    config: {
      immediate: true,
      handler() {
        this.applyConfig();
      }
    }
  },

  methods: {
    applyConfig() {
      const config = this.config || {};
      Object.keys(this.form).forEach((name) => {
        if (typeof config[name] === 'undefined') {
          return;
        }
        const value = config[name];
        this.form[name] =
          name === 'clientToStbMedicalConditionMap' && value && typeof value === 'object'
            ? JSON.stringify(value, null, 2)
            : value;
      });

      this.testConnectionAt = config.testConnectionAt || '';
      this.testConnectionFailedStatus = config.testConnectionFailedStatus || '';

      this.$emit('update:valid', this.isValid());
    },

    onTextInput(name, label) {
      this.errors[name] =
        !this.form[name] || `${this.form[name]}`.length === 0 ? [`${label} is required`] : [];
      this.emitConfig();
    },

    onJsonInput(name, label) {
      const value = this.form[name];
      const fieldErrors = [];

      if (typeof value !== 'undefined' && `${value}` !== '') {
        let parsedJson;
        try {
          parsedJson = JSON.parse(value);
        } catch (_err) {
          fieldErrors.push(`${label} needs to be a valid JSON`);
        }
        if (parsedJson && Object.keys(parsedJson).length <= 0) {
          fieldErrors.push(`${label} needs to be a non-empty JSON`);
        }
      }

      if (fieldErrors.length > 0) {
        this.errorHash = { ...this.errorHash, [name]: true };
      } else {
        const nextHash = { ...this.errorHash };
        delete nextHash[name];
        this.errorHash = nextHash;
      }

      this.errors[name] = fieldErrors;
      this.emitConfig();
    },

    emitConfig(edited = true) {
      const config = cloneDeep(this.form);

      if (
        has(config, 'clientToStbMedicalConditionMap') &&
        !this.errorHash.clientToStbMedicalConditionMap
      ) {
        config.clientToStbMedicalConditionMap =
          config.clientToStbMedicalConditionMap === ''
            ? null
            : JSON.parse(config.clientToStbMedicalConditionMap);
      }

      this.$emit(
        'update:config',
        {
          apiEndpoint: config.apiEndpoint,
          schoolId: config.schoolId,
          tokenKey: config.tokenKey,
          clientToStbMedicalConditionMap: config.clientToStbMedicalConditionMap,
          testConnectionAt: this.testConnectionAt,
          testConnectionFailedStatus: this.testConnectionFailedStatus
        },
        edited
      );
      this.$emit('update:error-hash', this.errorHash);
      this.$emit('update:valid', this.isValid());
    },

    isValid() {
      const textValid = REQUIRED_TEXT_FIELDS.every(
        (name) => this.form[name] && `${this.form[name].trim()}`.length > 0
      );
      return textValid && Object.keys(this.errorHash).length === 0;
    },

    async testConnection() {
      return this.$app.stbApiAdapter.getClientStudentTestConnection(
        'wonde',
        this.form.apiEndpoint,
        this.form.tokenKey,
        this.form.schoolId
      );
    },

    async onClickTestConnection() {
      this.testConnectionInProgress = true;

      const result = await this.testConnection();

      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      this.testConnectionInProgress = false;
      this.genericDialogDisplayed = true;

      if (!result.error) {
        this.testConnectionAt = result.data.connectionTestedAt;
        this.testConnectionFailedStatus = '';
        this.isGenericDialogDisplayedAsError = false;
        this.genericDialogMessage = [
          `Successfully connect with student management vendor for school "${result.data.schoolName}".`,
          '<br/><br/>',
          'Please click "Save" to save the current settings.'
        ].join('');
      } else {
        this.testConnectionAt = this.$app.moment().toISOString();
        this.testConnectionFailedStatus = result.message;
        this.isGenericDialogDisplayedAsError = true;
        this.genericDialogMessage = result.message;
      }

      this.emitConfig(false);
    },

    async testConnectionBeforeSave() {
      const result = await this.testConnection();

      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);

      if (!result.error) {
        this.testConnectionAt = result.data.connectionTestedAt;
        this.testConnectionFailedStatus = '';
        this.emitConfig(false);
        return true;
      }

      this.testConnectionAt = this.$app.moment().toISOString();
      this.testConnectionFailedStatus = result.message;
      this.emitConfig(false);
      return false;
    }
  }
};
</script>
